import React from 'react';
import {
  Container, Typography, Box, TextField, Button, Grid, Paper,
  Stack, useTheme, alpha, IconButton, Divider
} from '@mui/material';
import { motion } from 'framer-motion';
import EmailIcon from '@mui/icons-material/Email';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import SendIcon from '@mui/icons-material/Send';
import GitHubIcon from '@mui/icons-material/GitHub';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import SEO from '../components/SEO';

function Contact() {
  const theme = useTheme();
  const isDark = theme.palette.mode === 'dark';

  return (
    <Box sx={{ minHeight: '125vh', bgcolor: 'background.default', py: { xs: 6, md: 10 } }}>
      <SEO
        title="Contact Us | UmbrellaCo"
        description="Get in touch with the UmbrellaCo team for feedback, support, or feature requests."
        url="/contact"
      />
      <Container maxWidth="lg">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Paper
            elevation={isDark ? 0 : 4}
            sx={{
              borderRadius: 4,
              overflow: 'hidden',
              bgcolor: 'background.paper',
              border: isDark ? `1px solid ${alpha('#fff', 0.1)}` : 'none',
              boxShadow: isDark ? `0 20px 40px ${alpha('#000', 0.5)}` : `0 20px 40px ${alpha(theme.palette.primary.main, 0.1)}`
            }}
          >
            <Grid container>
              {/* Left Panel - Info */}
              <Grid item xs={12} md={5}>
                <Box
                  sx={{
                    height: '100%',
                    p: { xs: 4, md: 6 },
                    background: isDark
                      ? `linear-gradient(135deg, ${alpha(theme.palette.primary.dark, 0.8)} 0%, ${alpha(theme.palette.secondary.dark, 0.8)} 100%)`
                      : `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
                    color: 'white',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between'
                  }}
                >
                  <Box>
                    <Typography variant="h4" fontWeight={900} sx={{ mb: 2 }}>
                      Get in Touch
                    </Typography>
                    <Typography variant="body1" sx={{ color: alpha('#fff', 0.8), mb: 6, lineHeight: 1.7 }}>
                      Have a question, feedback, or a feature request? We'd love to hear from you.
                      Fill out the form and our team will get back to you shortly.
                    </Typography>

                    <Stack spacing={4}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ p: 1.5, bgcolor: alpha('#fff', 0.1), borderRadius: 2 }}>
                          <EmailIcon />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), display: 'block' }}>Email</Typography>
                          <Typography variant="body1" fontWeight={600}>hello@umbrellaco.in</Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Box sx={{ p: 1.5, bgcolor: alpha('#fff', 0.1), borderRadius: 2 }}>
                          <LocationOnIcon />
                        </Box>
                        <Box>
                          <Typography variant="caption" sx={{ color: alpha('#fff', 0.6), display: 'block' }}>HQ</Typography>
                          <Typography variant="body1" fontWeight={600}>San Francisco, CA</Typography>
                        </Box>
                      </Box>
                    </Stack>
                  </Box>

                  <Box sx={{ mt: 8 }}>
                    <Typography variant="body2" sx={{ color: alpha('#fff', 0.6), mb: 2, fontWeight: 600 }}>
                      CONNECT WITH US
                    </Typography>
                    <Stack direction="row" spacing={2}>
                      <IconButton sx={{ color: 'white', bgcolor: alpha('#fff', 0.1), '&:hover': { bgcolor: alpha('#fff', 0.2) } }}>
                        <GitHubIcon />
                      </IconButton>
                      <IconButton sx={{ color: 'white', bgcolor: alpha('#fff', 0.1), '&:hover': { bgcolor: alpha('#fff', 0.2) } }}>
                        <TwitterIcon />
                      </IconButton>
                      <IconButton sx={{ color: 'white', bgcolor: alpha('#fff', 0.1), '&:hover': { bgcolor: alpha('#fff', 0.2) } }}>
                        <LinkedInIcon />
                      </IconButton>
                    </Stack>
                  </Box>
                </Box>
              </Grid>

              {/* Right Panel - Form */}
              <Grid item xs={12} md={7}>
                <Box
                  component="form"
                  noValidate
                  autoComplete="off"
                  sx={{
                    p: { xs: 4, md: 6 },
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'center'
                  }}
                >
                  <Typography variant="h5" fontWeight={800} sx={{ mb: 4, color: 'text.primary' }}>
                    Send us a message
                  </Typography>

                  <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        variant="filled"
                        sx={{
                          '& .MuiFilledInput-root': {
                            bgcolor: isDark ? alpha('#fff', 0.05) : alpha(theme.palette.primary.main, 0.03),
                            borderRadius: 2,
                            '&:before': { display: 'none' },
                            '&:after': { display: 'none' },
                            '&:hover': { bgcolor: isDark ? alpha('#fff', 0.08) : alpha(theme.palette.primary.main, 0.06) },
                            '&.Mui-focused': { bgcolor: isDark ? alpha('#fff', 0.08) : alpha(theme.palette.primary.main, 0.06), border: `1px solid ${theme.palette.primary.main}` }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        variant="filled"
                        sx={{
                          '& .MuiFilledInput-root': {
                            bgcolor: isDark ? alpha('#fff', 0.05) : alpha(theme.palette.primary.main, 0.03),
                            borderRadius: 2,
                            '&:before': { display: 'none' },
                            '&:after': { display: 'none' },
                            '&:hover': { bgcolor: isDark ? alpha('#fff', 0.08) : alpha(theme.palette.primary.main, 0.06) },
                            '&.Mui-focused': { bgcolor: isDark ? alpha('#fff', 0.08) : alpha(theme.palette.primary.main, 0.06), border: `1px solid ${theme.palette.primary.main}` }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Work Email"
                        variant="filled"
                        sx={{
                          '& .MuiFilledInput-root': {
                            bgcolor: isDark ? alpha('#fff', 0.05) : alpha(theme.palette.primary.main, 0.03),
                            borderRadius: 2,
                            '&:before': { display: 'none' },
                            '&:after': { display: 'none' },
                            '&:hover': { bgcolor: isDark ? alpha('#fff', 0.08) : alpha(theme.palette.primary.main, 0.06) },
                            '&.Mui-focused': { bgcolor: isDark ? alpha('#fff', 0.08) : alpha(theme.palette.primary.main, 0.06), border: `1px solid ${theme.palette.primary.main}` }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="How can we help?"
                        variant="filled"
                        multiline
                        rows={5}
                        sx={{
                          '& .MuiFilledInput-root': {
                            bgcolor: isDark ? alpha('#fff', 0.05) : alpha(theme.palette.primary.main, 0.03),
                            borderRadius: 2,
                            '&:before': { display: 'none' },
                            '&:after': { display: 'none' },
                            '&:hover': { bgcolor: isDark ? alpha('#fff', 0.08) : alpha(theme.palette.primary.main, 0.06) },
                            '&.Mui-focused': { bgcolor: isDark ? alpha('#fff', 0.08) : alpha(theme.palette.primary.main, 0.06), border: `1px solid ${theme.palette.primary.main}` }
                          }
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sx={{ mt: 2 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        size="large"
                        endIcon={<SendIcon />}
                        sx={{
                          py: 1.5,
                          px: 4,
                          borderRadius: 2,
                          fontWeight: 700,
                          boxShadow: `0 8px 16px ${alpha(theme.palette.primary.main, 0.3)}`,
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 12px 20px ${alpha(theme.palette.primary.main, 0.4)}`,
                          }
                        }}
                      >
                        Send Message
                      </Button>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
            </Grid>
          </Paper>
        </motion.div>
      </Container>
    </Box>
  );
}

export default Contact;